import { DataType, TablesData } from "../../../tables";
import { TableName } from "../../../tables/types/types";
import { usePermissionsActions } from "./permissionsActions";
import { firstItem } from "../../../utils/listUtils";
import { Data2, Standards } from "../../../features/data-types";
import { tableAction, tableArn } from "../../../utils/policiesUtils";
import { AttributeMap, useDynamoActions } from "../../../services/dynamo"
import { useChangedIdMigration } from "../../../guides/migrations/changedId";
import { useRemovedPropMigration } from "../../../guides/migrations/removedProp";
import { StatementType } from "../../../services/iam";
import { useAddedRequiredPropMigration } from "../../../guides/migrations/addedRequiredProp";
import { useRenamedPropMigration } from "../../../guides/migrations/renamedProp";
import { useChangedEnumValueMigration } from "../../../guides/migrations/changedEnumValue";
import { useRemovedTableTenantKeyMigration } from "../../../guides/migrations/removedTableTenantKey";
import { Company, RequestStatus, systemPermissions, SystemPolicyName, User, UserRole, useSystemActions, useRequestLoaders, useCompanyBuilders } from "../../system";
import { showErrorStrToast } from "../../../features/ui";
import { administrationPermissions, VatCondition } from "../../administration";
import { purchasesPermissions } from "../../purchases";
import { managerPermissions, useBranchBuilders, useBranchActions, useManagerActions } from "../../manager";
import { warehousePermissions } from "../../warehouse";
import { TableActions, policyName, roleName, toIamPermissions, toPermissionList } from "../../../features/roles";
import { DynamoUserApi } from "../../../services";
import { useImportSuppliers, useImportCustomers, useImportProducts } from "../../../features/importer";
import { useUpdatedValueMigration } from "../../../guides/migrations/updatedValue";
import { awsS3Bucket, awsS3PublicBucket } from "../../../services/constants";
import { useRenamedTableMigration } from "../../../guides/migrations/renamedTable";
import { construfyAdminPermissions, ConstrufyAdminPolicyName } from "../permissions/permissions";
import { logisticsPermissions } from "../../logistics";
import { useAddressBuilders } from "../../../features/address";
import { salesPermissions } from "../../sales";
import { useFetchActions, useSaveActions } from "../../common";
import { Operation } from "../../operations";
import { Delivery, DeliveryOrder, deliveryHelpers } from "../../deliveries";

export const useConstrufyAdminActions = () => {
    const { buildBranch } = useBranchBuilders()
    const { buildAddress } = useAddressBuilders()
    const { buildCompany } = useCompanyBuilders()
    const requestLoaders = useRequestLoaders()
    const systemActions = useSystemActions()
    const managerActions = useManagerActions()
    const { saveBranch } = useBranchActions()
    const permissionsActions = usePermissionsActions()
    const dynamoActions = useDynamoActions()
    const changedId = useChangedIdMigration()
    const removedProp = useRemovedPropMigration()
    const renamedProp = useRenamedPropMigration()
    const addedRequiredProp = useAddedRequiredPropMigration()
    const updatedValue = useUpdatedValueMigration()
    const changedEnumValue = useChangedEnumValueMigration()
    const removedTableTenantKey = useRemovedTableTenantKeyMigration()
    const renamedTable = useRenamedTableMigration()
    const importSuppliers = useImportSuppliers()
    const importCustomers = useImportCustomers()
    const importProducts = useImportProducts()
    
    const createCompany = async (
        code: string,
        name: string,
        legalName: string,
        taxId: number,
        email: string,
        addressData: Data2,
        websiteUrl?: string
    ) => {
        const { fetchCompanyByCode, saveCompany } = systemActions()
        const company = await fetchCompanyByCode(code)
        if (company) {
            showErrorStrToast('La compañía ya existe.')
        } else {
            const address = buildAddress(addressData)
            const data = {
                code,
                name,
                legalName,
                taxIds: [{ taxId, main: true }],
                websiteUrl,
                email,
                address,
                vatCondition: VatCondition.REGISTERED_TAXPAYER
            }
            const newCompany = buildCompany(data)
            saveCompany(newCompany)
        }
    }

    const createBranch = async (
        companyId: string,
        code: string,
        name: string,
        addressText: string,
        locality: string,
        province: string
    ) => {
        const address = buildAddress({ address: addressText, locality, province })
        const branch = buildBranch({ companyId, code, name, address })
        await saveBranch(branch)
    }

    const resolveRequest = async (
        companyCode: string,
        userEmail: string,
        resolution: RequestStatus
    ) => {
        const company = await systemActions().fetchCompanyByCode(companyCode)
        const user = await systemActions().fetchUserByEmail(userEmail)
        const { loadUserRequests } = requestLoaders(company!.id)
        const requests = await loadUserRequests(user!.id)
        managerActions().resolveRequest(firstItem(requests)!, company!, resolution, false)
    }

    const saveSystemPolicy = () => {
        const tablesStatements: StatementType[] = []
        toIamPermissions(toPermissionList(systemPermissions())).forEach(permission => {
            tablesStatements.push({
                Effect: "Allow",
                Action: permission.actions.map(action => tableAction(action)),
                Resource: permission.tables.map(table => tableArn(table))
            })
        })

        const policy = {
            "Version": "2012-10-17",
            "Statement": tablesStatements.concat(
                {
                    "Effect": "Allow",
                    "Action": [
                        "iam:AddUserToGroup",
                        "iam:CreateAccessKey",
                        "iam:CreateUser",
                        "iam:DeleteUser",
                        "iam:GetUser",
                        "iam:ListGroupsForUser",
                        "iam:RemoveUserFromGroup",
                        "iam:PassRole",
                        "sts:AssumeRole"
                    ],
                    "Resource": [
                        "*"
                    ]
                },
                {
                    "Effect": "Allow",
                    "Action": ["s3:*"],
                    "Resource": [
                        `arn:aws:s3:::${awsS3Bucket}/*`,
                        `arn:aws:s3:::${awsS3PublicBucket}/*`
                    ]
                },
                {
                    "Effect": "Allow",
                    "Action": [
                        "scheduler:CreateSchedule",
                        "scheduler:DeleteSchedule",
                        "scheduler:GetSchedule",
                        "scheduler:ListSchedules"
                    ],
                    "Resource": "*"
                }
            )
        }

        permissionsActions.savePolicyFromJson(SystemPolicyName, policy)
    }

    const saveConstrufyAdminPolicy = () => {
        const tablesStatements: StatementType[] = []
        toIamPermissions(toPermissionList(construfyAdminPermissions())).forEach(permission => {
            tablesStatements.push({
                Effect: "Allow",
                Action: permission.actions.map(action => tableAction(action)),
                Resource: permission.tables.map(table => tableArn(table))
            })
        })

        const policy = {
            "Version": "2012-10-17",
            "Statement": tablesStatements.concat({
                "Effect": "Allow",
                "Action": [
                    "dynamodb:CreateTable",
                    "dynamodb:DeleteTable",
                    "iam:AddUserToGroup",
                    "iam:AttachGroupPolicy",
                    "iam:AttachRolePolicy",
                    "iam:CreateGroup",
                    "iam:CreatePolicy",
                    "iam:CreateRole",
                    "iam:CreatePolicyVersion",
                    "iam:CreateUser",
                    "iam:DeletePolicyVersion",
                    "iam:GetUser",
                    "iam:ListPolicyVersions"
                ],
                "Resource": "*"
            })
        }

        permissionsActions.savePolicyFromJson(ConstrufyAdminPolicyName, policy)
        // permissionsActions.createRole(ConstrufyAdminRoleName, ConstrufyAdminPolicyName)
    }

    const createCompanyPolicies = async (companyId?: string) => {
        const createPolicy = async (
            company: Company,
            userRole: UserRole,
            permissions: TableActions[]
        ) => {
            const policy = policyName(company.code, userRole)
            await permissionsActions.savePolicy(policy, toPermissionList(permissions), company.id, true)
            const role = roleName(company.code, userRole)
            await permissionsActions.createRole(role, policy)
        }

        const companies = await systemActions().fetchAllCompanies()
        companies.forEach(async company => {
            if (!companyId || companyId === company.id) {
                await createPolicy(company, UserRole.ADMINISTRATION, administrationPermissions())
                await createPolicy(company, UserRole.LOGISTICS, logisticsPermissions())
                await createPolicy(company, UserRole.MANAGER, managerPermissions())
                await createPolicy(company, UserRole.PURCHASES, purchasesPermissions())
                await createPolicy(company, UserRole.SALES, salesPermissions())
                await createPolicy(company, UserRole.WAREHOUSE, warehousePermissions())
            }
        })
    }

    const saveCompanyPolicies = async (companyId?: string) => {
        const companies = await systemActions().fetchAllCompanies()
        companies.forEach(company => {
            if (!companyId || companyId === company.id) {
                const adminstrationPolicy = policyName(company.code, UserRole.ADMINISTRATION)
                const logisticsPolicy = policyName(company.code, UserRole.LOGISTICS)
                const managerPolicy = policyName(company.code, UserRole.MANAGER)
                const purchasesPolicy = policyName(company.code, UserRole.PURCHASES)
                const salesPolicy = policyName(company.code, UserRole.SALES)
                const warehousePolicy = policyName(company.code, UserRole.WAREHOUSE)

                permissionsActions.savePolicy(adminstrationPolicy, toPermissionList(administrationPermissions()), company.id)
                permissionsActions.savePolicy(logisticsPolicy, toPermissionList(logisticsPermissions()), company.id)
                permissionsActions.savePolicy(managerPolicy, toPermissionList(managerPermissions()), company.id)
                permissionsActions.savePolicy(purchasesPolicy, toPermissionList(purchasesPermissions()), company.id)
                permissionsActions.savePolicy(salesPolicy, toPermissionList(salesPermissions()), company.id)
                permissionsActions.savePolicy(warehousePolicy, toPermissionList(warehousePermissions()), company.id)
            }
        })
    }

    const createTables = () => {
        const tableParams = (tableName: TableName, hasTKey: boolean, secondaryIndexes?: string[]) => ({
            name: TablesData.get(tableName)!.dbTableName,
            hasTKey,
            secondaryIndexes
        })
        const tables = [
            // tableParams(TableName.BRANCHES, true),
            // tableParams(TableName.BUDGETS, true),
            // tableParams(TableName.CASH_FUNDS, true),
            // tableParams(TableName.CATEGORIES, true),
            // tableParams(TableName.CHECKS, true),
            // tableParams(TableName.COMPANIES, false),
            // tableParams(TableName.CREDIT_CARDS, true),
            // tableParams(TableName.CREDIT_PAYMENTS, true),
            // tableParams(TableName.CUSTOMERS, true),
            // tableParams(TableName.CUSTOMER_TYPES, true),
            tableParams(TableName.DELIVERY_ORDERS, true), // Index/*
            tableParams(TableName.DELIVERIES2, true),
            // tableParams(TableName.DELIVERY_ROADMAPS, true, ['deliveryId', 'roadmapId']),
            // tableParams(TableName.DRIVERS, true),
            // tableParams(TableName.EMPLOYEES, true),
            // tableParams(TableName.EXPENSES, true),
            // tableParams(TableName.EXPENSE_TYPES, true),
            // tableParams(TableName.FISCAL_DOCUMENTS, true),
            // tableParams(TableName.HOARD_ORDERS, true), // Index/*
            // tableParams(TableName.LOTS, true),
            // tableParams(TableName.PAYMENT_ORDERS, true),
            // tableParams(TableName.PAYMENT_TYPES, true),
            // tableParams(TableName.PRODUCT_PRICES, true),
            // tableParams(TableName.PRODUCTS, true),
            // tableParams(TableName.PURCHASE_ORDERS, true),
            // tableParams(TableName.REQUESTS, true),
            // tableParams(TableName.ROADMAPS, true),
            // tableParams(TableName.SALE_ORDERS, true), // Index/*
            // tableParams(TableName.SETTINGS, true),
            // tableParams(TableName.STOCK, true),
            // tableParams(TableName.SUPPLIERS, true),
            // tableParams(TableName.TAXES, true),
            // tableParams(TableName.TICKETS, true),
            // tableParams(TableName.TRANSACTIONS, true),
            // tableParams(TableName.UNIT_TYPES, false),
            // tableParams(TableName.USERS, false),
            // tableParams(TableName.VEHICLES, true)
        ]
        DynamoUserApi.createTables(tables)
    }

    const createUnitTypes = async () => {
        const defaultUnitTypesDT = Standards.unitTypes() as DataType[]
        const UnitTypesData = TablesData.get(TableName.UNIT_TYPES)!
        await DynamoUserApi.createOrUpdate(UnitTypesData.dbTableName, defaultUnitTypesDT as AttributeMap[])
    }

    const createDefaultData = async (companyIdParam?: string) => {
        const createData = async (companyId: string) => {
            const { createOrUpdate } = dynamoActions(DynamoUserApi, companyId)

            const defaultTaxes = Standards.taxes(companyId)
            const defaultBranches = Standards.branches(companyId)
            const defaultCategories = Standards.categories(companyId)
            const defaultCustomerTypes = Standards.customerTypes(companyId)
            const defaultPaymentTypes = Standards.paymentTypes(companyId)
            const defaultExpenseTypes = Standards.expenseTypes(companyId)
            const defaultSettings = Standards.settings(companyId)

            const defaultBranchesDT = defaultBranches as DataType[]
            const BranchesData = TablesData.get(TableName.BRANCHES)!
            await createOrUpdate(BranchesData.dbTableName, defaultBranchesDT as AttributeMap[])
            
            const defaultCategoriesDT = defaultCategories as DataType[]
            const CategoriesData = TablesData.get(TableName.CATEGORIES)!
            await createOrUpdate(CategoriesData.dbTableName, defaultCategoriesDT as AttributeMap[])
            
            const defaultCustomerTypesDT = defaultCustomerTypes as DataType[]
            const CustomerTypesData = TablesData.get(TableName.CUSTOMER_TYPES)!
            await createOrUpdate(CustomerTypesData.dbTableName, defaultCustomerTypesDT as AttributeMap[])
            
            const defaultExpenseTypesDT = defaultExpenseTypes as DataType[]
            const ExpenseTypesData = TablesData.get(TableName.EXPENSE_TYPES)!
            await createOrUpdate(ExpenseTypesData.dbTableName, defaultExpenseTypesDT as AttributeMap[])

            const defaultPaymentTypesDT = defaultPaymentTypes as DataType[]
            const PaymentTypesData = TablesData.get(TableName.PAYMENT_TYPES)!
            await createOrUpdate(PaymentTypesData.dbTableName, defaultPaymentTypesDT as AttributeMap[])

            const defaultTaxesDT = defaultTaxes as DataType[]
            const TaxesData = TablesData.get(TableName.TAXES)!
            await createOrUpdate(TaxesData.dbTableName, defaultTaxesDT as AttributeMap[])

            const defaultSettingsDT = defaultSettings as DataType[]
            const SettingsData = TablesData.get(TableName.SETTINGS)!
            await createOrUpdate(SettingsData.dbTableName, defaultSettingsDT as AttributeMap[])
        }

        if (companyIdParam) {
            createData(companyIdParam)
        } else {
            const companies = await systemActions().fetchAllCompanies()
            companies.forEach(({ id }) => createData(id))
        }
    }

    const clearTable = async (companyId: string, tableName: TableName) => {
        const { getAll, remove } = dynamoActions(DynamoUserApi, companyId)
        const { dbTableName } = TablesData.get(tableName)!
        const dataTypes = await getAll(dbTableName) as DataType[]
        const ids = dataTypes.map(dataType => dataType.id)
        await remove(dbTableName, ids)
    }

    const findReferences = async (companyId: string, ids: string[]) => {
        const tables = Object.values(TableName).filter(table => ![
            TableName.USERS,
            TableName.UNIT_TYPES,
            TableName.COMPANIES,
            TableName.ADMIN,
            TableName.ADDRESSES
        ].includes(table))

        const { getAll, } = dynamoActions(DynamoUserApi, companyId)

        tables.forEach(async table => {
            const dataTypes = await getAll(table) as DataType[]
            const matches: DataType[] = []
            dataTypes.forEach(dataType => {
                const str = JSON.stringify(dataType)
                if (ids.some(id => str.indexOf(id) !== -1)) {
                    matches.push(dataType)
                }
            })
            if (matches.length > 0) {
                console.log('------------------------------------------------')
                console.log(table, matches.map(dataType => dataType.id))
                console.log('TOTAL', table, matches.length)
            }
        })
    }

    const fetchActions = useFetchActions()
    const saveActions = useSaveActions()
    const migrate = async () => {
        const companies = await systemActions().fetchAllCompanies()
        const companyIds = companies.map(({ id }) => id)
        
        companyIds.forEach(async companyId => {
            // (SaleOrder | HoardOrder | PurchaseOrder).items.deliveryStats
            const { fetchAll } = fetchActions(companyId)
            const { saveMultiple } = saveActions(companyId)

            const tables = [TableName.SALE_ORDERS, TableName.HOARD_ORDERS, TableName.PURCHASE_ORDERS]
            tables.forEach(table => {
                fetchAll(table).then(l => {
                    const newL = l.map(dt => {
                        const i = dt as Operation
                        const newItems = i.items.map(item => ({
                            ...item,
                            deliveryStats: {
                                toBeEmitted: 0,
                                pending: 0,
                                emitted: 0
                            }
                        }))
                        return { ...i, items: newItems }
                    })
                    
                    console.log(table, newL)
                    saveMultiple(table, newL)
                })
            })
        })
    }

    // Deliveries2.codeStr: delivery notes created before the prop existed can't be found by number in the delivery notes list
    const migrateDeliveryNotesCode = async () => {
        const companies = await systemActions().fetchAllCompanies()
        for (const { id: companyId } of companies) {
            const { fetchAll } = fetchActions(companyId)
            const { saveMultiple } = saveActions(companyId)
            const deliveries = await fetchAll(TableName.DELIVERIES2) as Delivery[]
            const deliveryOrders = await fetchAll(TableName.DELIVERY_ORDERS) as DeliveryOrder[]
            const updatedDeliveries = deliveries
                .filter(delivery => !delivery.codeStr)
                .map(delivery => {
                    const deliveryOrder = deliveryOrders.find(deliveryOrder => deliveryOrder.id === delivery.orderId)
                    return { ...delivery, codeStr: deliveryHelpers.describeDeliveryCode(delivery, deliveryOrder) }
                })
            console.log(companyId, TableName.DELIVERIES2, updatedDeliveries.length)
            if (updatedDeliveries.length > 0) {
                await saveMultiple(TableName.DELIVERIES2, updatedDeliveries)
            }
        }
    }

    const removeUser = async (userEmail: string) => {
        const users = await systemActions().fetchUsersByEmail(userEmail) as User[]
        const user = firstItem(users)! as User
        const userId = user.id
        
        await systemActions().removeUser(userId)
        
        const { loadUserRequests } = requestLoaders()
        const requests = await loadUserRequests(userId)
        requests.forEach(async request => {
            await systemActions(request.companyId).removeRequest(request.id)
        })
    }

    return {
        createCompany,
        createBranch,
        resolveRequest,
        saveConstrufyAdminPolicy,
        saveSystemPolicy,
        createCompanyPolicies,
        saveCompanyPolicies,
        createTables,
        createUnitTypes,
        createDefaultData,
        clearTable,
        findReferences,
        migrate,
        migrateDeliveryNotesCode,
        removeUser,
        importSuppliers,
        importCustomers,
        importProducts
    }
}
